import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/auth-context';
import type {
    AuthUser,
    GitHubExportOptions,
    GitHubExportResult
} from '../api-types';

// Simplified types for CSR
type GitHubExportProgress = {
  type: 'github_export_progress';
  data: {
    step: string;
    progress: number;
  };
};

export interface GitHubExportState {
    isExporting: boolean;
    progress?: GitHubExportProgress;
    result?: GitHubExportResult;
    isModalOpen: boolean;
}

export interface GitHubInstallationData {
    installationId: number;
    username: string;
    repositories?: string[];
}

export function useGitHubExport(_websocket?: unknown, _agentId?: string): {
    isExporting: boolean;
    progress?: GitHubExportProgress;
    result?: GitHubExportResult;
    isModalOpen: boolean;
    openModal: () => void;
    closeModal: () => void;
    startExport: (options: GitHubExportOptions) => Promise<void>;
    isAuthenticated: boolean;
    user: AuthUser | null;
    retry: () => void;
} {
    const { user, isAuthenticated } = useAuth();
    const [state, setState] = useState<GitHubExportState>({
        isExporting: false,
        isModalOpen: false
    });

    // Open the export modal
    const openModal = useCallback(() => {
        setState(prev => ({
            ...prev,
            isModalOpen: true,
            result: undefined // Clear any previous results
        }));
    }, []);

    // Close the export modal
    const closeModal = useCallback(() => {
        setState(prev => ({
            ...prev,
            isModalOpen: false,
            isExporting: false,
            progress: undefined,
            result: undefined
        }));
    }, []);

    // Start GitHub export (simplified for CSR)
    const startExport = useCallback(async (_options: GitHubExportOptions) => {
        // For CSR architecture, GitHub export is not available
        // This would require a backend service to handle OAuth and repository creation
        setState(prev => ({
            ...prev,
            isExporting: false,
            result: {
                success: false,
                error: 'GitHub export is not available in client-side mode. Please download your files manually.'
            }
        }));
    }, []);

    // Retry function (simplified)
    const retry = useCallback(() => {
        setState(prev => ({
            ...prev,
            result: undefined,
            progress: undefined
        }));
    }, []);

    return {
        ...state,
        openModal,
        closeModal,
        startExport,
        isAuthenticated,
        user,
        retry
    };
}